"use client";
import { useCallback, useEffect } from "react";
import { useAuthStore } from "@/_stores/useAuthStore";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { removeCookie, setCookie } from "@/app/api/jwt/fetcher";

let initializeAuthInFlight: Promise<void> | null = null;

export const useInitializeAuth = (autoFetchUser = true) => {
  const { setLoading, initializeFromCookie, isLoading } = useAuthStore();
  const { data: session } = useSession();
  const pathName = usePathname();

  const isAuthorizedPage = pathName.includes("/auth/authorized");
  const isExcludedPage = pathName.includes("/auth/login");

  const fetchCurrentUser = useCallback(
    async (isForce = false) => {
      if (!isForce && initializeAuthInFlight) {
        await initializeAuthInFlight;
        return;
      }

      const runInitialization = async () => {
        if (isForce) {
          setLoading(true);
        }
        try {
          if (session?.bffToken && session?.userId && session?.expiredAt) {
            await setCookie(
              session.bffToken,
              session.userId,
              session.expiredAt,
              session.roleId,
            );
          }

          if (!isExcludedPage) {
            await initializeFromCookie(isForce);
          }
        } catch (error) {
          // For authorized page we will let it handle the 403 error itself
          if ((error as Error).message.includes("403") && !isAuthorizedPage) {
            removeCookie();
          }
        } finally {
          setLoading(false);
        }
      };

      initializeAuthInFlight = runInitialization();
      if (isForce) {
        await initializeAuthInFlight;
        initializeAuthInFlight = null;
        return;
      }
      await initializeAuthInFlight.finally(() => {
        initializeAuthInFlight = null;
      });
    },
    [
      initializeFromCookie,
      setLoading,
      session,
      pathName,
      isAuthorizedPage,
      isExcludedPage,
    ],
  );

  useEffect(() => {
    if (autoFetchUser) {
      void fetchCurrentUser();
    }
  }, [autoFetchUser, fetchCurrentUser]);

  return {
    fetchCurrentUser,
    isLoading,
  };
};
